import { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import { DeepChat } from 'deep-chat-react'
import './styles/theme.css'
import './styles/deepchat.overrides.css'

import { TopBar } from './components/layout/TopBar'
import { WipSidebar } from './components/sidebar/WipSidebar'
import { PillsSidebar } from './components/sidebar/PillsSidebar'

import { ChatPanel } from './components/chat/ChatPanel'
import {AppShell} from "./components/layout/AppShell";

import { useParams } from "react-router-dom";
import { ProjectProvider } from "./state/project";
import { useProject } from "./state/project";
import {GeneratorPanel} from "./components/3dGenerator/GeneratorPanel";
import {Footer} from "./components/Footer";

export const App = () => {
    const { projectId } = useParams();

    if (!projectId) return null;

    return (
        <ProjectProvider projectId={projectId}>
            <AppInner />
        </ProjectProvider>
    );
};


export const AppInner = () => {

    const { project, applyAiResponseAndSave,addAttachmentAndSave, appendIncomingMessages,saveNow,isLoading } = useProject();

    const [currentPhaseIndex, setCurrentPhaseIndex] = useState<number>(1)

    //state per l'immagine 2D che viene mostrata nella fase 4
    const  [generatedImage, setGeneratedImage] = useState<string | null>(null);

    const hydratedUiRef = useRef(false);

    // ✅ ref sempre aggiornati (evitano closure vecchie nei callback “stabili”)
    const currentPhaseIndexRef = useRef<number>(1);

    useEffect(() => {
        currentPhaseIndexRef.current = currentPhaseIndex;
    }, [currentPhaseIndex]);

    const [brief, setBrief] = useState<string>("")

    /* ====== STATE specific for phase 2 ====== */
    const [technicalKeywords, setTechnicalKeywords] = useState<string[]>([])
    const [palette, setPalette] = useState<string[]>([])

    /* ====== ISPIRAZIONI PER la fase 1 ====== */
    const baseInspirations = [
        "Geometria sacra", "Architettura brutalista", "Onde sonore", "Ghiaccio e cristalli",
        "Arte déco", "Minimalismo giapponese", "Cinema noir", "Frattali", "Origami",
        "Arte generativa", "Costellazione", "Vetrate gotiche"
    ]

    const randomInspirations = useMemo(() => {
        return [...baseInspirations].sort(() => 0.5 - Math.random()).slice(0, 4)
    }, [])

    function generateButtonsHtml(randomInspirations) {
        return `
    <div class="deep-chat-temporary-message" style="
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 2px;
  ">
      ${randomInspirations.map(i =>
            `<button
      class="deep-chat-button deep-chat-suggestion-button"
      style="
        display: inline-flex;
        align-items: center;
        gap: 4px;

        background: rgba(10,6,22,.35);
        border: 1px solid rgba(255,77,26,.95);
        border-radius: 999px;

        padding: 6px 10px;
        font-size: 14px;
        font-weight: 500;
        line-height: 1;

        color: rgba(245,244,255,.95);
        cursor: pointer;

        box-shadow:
          0 0 0 1px rgba(0,0,0,.25) inset,
          0 14px 26px rgba(0,0,0,.20);

        transition: background .15s ease, box-shadow .15s ease, transform .05s ease;
      "
    
      onmouseenter="
        this.style.background='rgba(10,6,22,.55)';
        this.style.color='rgba(245,244,255,.95)';
        this.style.boxShadow='0 0 18px rgba(255,77,26,.25), 0 16px 28px rgba(0,0,0,.25)';
      "
    
      onmouseleave="
        this.style.background='rgba(10,6,22,.35)';
        this.style.color='rgba(245,244,255,.95)';
        this.style.boxShadow='0 0 0 1px rgba(0,0,0,.25) inset, 0 14px 26px rgba(0,0,0,.20)';
      "
    
      onfocus="
        this.style.background='rgba(10,6,22,.55)';
        this.style.color='rgba(245,244,255,.95)';
      "
    
      onblur="
        this.style.background='rgba(10,6,22,.35)';
        this.style.color='rgba(245,244,255,.95)';
      "
    
      onmousedown="
        this.style.transform='translateY(1px)';
      "
    
      onmouseup="
        this.style.transform='translateY(0)';
      "
    >
      <img
        src="/assets/img/Stars.svg"
        style="width:18px;height:18px;opacity:.95"
      />
      ${i}
    </button>`
        ).join('')}
    </div>
  `;
    }

    const phase1ButtonsHTML = useMemo(() => generateButtonsHtml(randomInspirations), [randomInspirations])

    const materialSuggestions = [
        "extrachiaro lucido",
        "satinato (acidato)",
        "sfaccettato molato",
        "fumé",
        "inciso a mano",
        "a doppia texture"
    ];

    const phase2ButtonsHTML = useMemo(() => generateButtonsHtml(materialSuggestions), [materialSuggestions])

    //creo i buttons per la fase 3 che corrisponde alla generazione visuale del bicchiere (a partire dal brief creatico e tecnico)
    const designSuggestions = [
        "schetch minimalista in bianco e nero",
        "bozzetto colorato con ombreggiature",
        "disegno tecnico dettagliato",
        "immagine realistica ambientata",
        "moodboard di ispirazione",
        ];
    const phase3ButtonsHTML = useMemo(() => generateButtonsHtml(designSuggestions), [designSuggestions])

    /*const briefSnapshotRef = useRef<string>("");

    useEffect(() => {
        // quando entri in fase 2, congela il brief corrente
        if (currentPhaseIndex === 2) {
            briefSnapshotRef.current = brief || "";
        }
    }, [currentPhaseIndex]); // <- SOLO fase, non brief*/

    const getInitialHistoryForPhase = useCallback((phase: number) => {
        if (phase === 1) {
            return [
                { text: 'Iniziamo trovando l’ispirazione.\n' +
                        'Puoi partire da un’idea generale, da un argomento o da una materia che ti affascina, oppure raccontare un’esperienza o un’emozione per te significativa.\n' +
                        'Se preferisci, per iniziare puoi anche provare scegliendo una delle ispirazioni di esempio qui sotto: sarà il nostro primo punto di partenza.', role: 'ai' },
                { html: phase1ButtonsHTML, role: 'ai' },
            ];
        }

        if (phase === 2) {
            const briefHTML = `<div style="white-space:pre-line"> ${brief ? brief : "Brief non disponibile."} </div>`
            return [
                { text: 'Ecco il brief creativo che abbiamo definito finora:', role: 'ai' },
                { html: briefHTML, role: 'ai' },
                { text: 'Iniziamo a scegliere il tipo di bicchiere. Quali tipologia di cristallo immagini per il tuo bicchiere?', role: 'ai' },
                { html: phase2ButtonsHTML, role: 'ai' },
            ];
        }

        if(phase === 3){
            const briefHTML = `<div style="white-space:pre-line"> ${brief ? brief : "Brief non disponibile."} </div>`

            const technicalBriefHTML = `<div style="white-space:pre-line">
                ${technicalKeywords.length > 0 ? "Parole chiave tecniche: " + technicalKeywords.join(", ") : "Nessuna parola chiave tecnica disponibile."} <br>
                ${palette.length > 0 ? "<div style='margin-top:8px;'>Palette di colori: #" + palette.join(", #") + "</div>" : ""}
            </div>`

            return [
                { text: 'Perfetto!! Ecco un riepilogo del brief creativo', role: 'ai' },
                { html: briefHTML, role: 'ai' },
                { text: 'E del brief tecnico con le parole chiave e la palette colori selezionate:', role: 'ai' },
                { html: technicalBriefHTML, role: 'ai' },
                { text: 'Ora che abbiamo definito il brief creativo e tecnico, possiamo procedere con la creazione dello schizzo del bicchiere. Che tipo di schizzo preferisci?', role: 'ai' },
                { html: phase3ButtonsHTML, role: 'ai' },
            ];
        }



        return [{ text: `Benvenuto nella fase ${phase}.`, role: 'ai' }];
    }, [phase1ButtonsHTML, phase2ButtonsHTML, currentPhaseIndex]);


    useEffect(() => {
        chatHistoryRef.current = getInitialHistoryForPhase(currentPhaseIndex);
    }, [currentPhaseIndex, getInitialHistoryForPhase]);


    const initialHistory = useMemo(
        () => getInitialHistoryForPhase(currentPhaseIndex),
        [currentPhaseIndex, getInitialHistoryForPhase]
    );


    //const historyRef = useRef(initialHistory)
    const chatHistoryRef = useRef<any[]>([]);
    const previousResponseIdRef = useRef<string | null>(null);


    useEffect(() => {
        // quando cambi fase, riparti dalla initialHistory visibile in chat
        chatHistoryRef.current = Array.isArray(initialHistory) ? [...initialHistory] : [];
        previousResponseIdRef.current = null;
    }, [currentPhaseIndex, initialHistory]);

    useEffect(() => {
        if (isLoading) return;
        if (!project) return;

        // ✅ solo al primo load del progetto (evita reset fase a ogni update)
        if (hydratedUiRef.current) return;
        hydratedUiRef.current = true;

        const phaseFromProject = project.current_phase || 1;
        setCurrentPhaseIndex(phaseFromProject);

        const ph = project.phases.find(p => p.index === phaseFromProject);
        if (!ph) return;

        setBrief(ph.brief || "");
        setBriefStatus((ph.status as any) || "incomplete");

        if (phaseFromProject === 2 && ph.technicalKeywords?.length) {
            setTechnicalKeywords(ph.technicalKeywords);
            setKeywords(ph.technicalKeywords);
        } else {
            setKeywords(ph.keywords || []);
            setTechnicalKeywords(ph.technicalKeywords || []);
        }

        setPalette(ph.palette || []);
    }, [isLoading, project]);


    /* ====== STATE ====== */
    const [briefStatus, setBriefStatus] = useState<'incomplete' | 'partial' | 'complete'>('incomplete')
    const [pillole, setPillole] = useState<string[]>([])
    //keywords
    const [keywords, setKeywords] = useState<string[]>([])


    type ChatMsg = { role: string; text: string };

    const toTextMessages = (arr: any[]): ChatMsg[] =>
        (arr || [])
            .filter((m) => m && !m.html) // scarta quick replies (html)
            .map((m) => ({
                role: (m.role || "user").toString(),
                text: (m.text || "").toString(),
            }))
            .filter((m) => m.text.trim().length > 0);

    const currentPhase = useMemo(() => {
        const phases = [
            'Brief Creativo',
            'Moodboard & Brief tecnico',
            'Schizzo e bozzetti',
            'Elaborazione 3D',
            //'Asset Grafici',
        ]
        return phases[currentPhaseIndex - 1]
    }
    , [currentPhaseIndex])




    const connect = useMemo(() => {
        const cfg = (window as any).__APP_CONFIG__;
        const base = cfg.CHAT_BASE.replace(/\/$/, "");

        const urlByPhase: Record<number, string> = {
            1: `${base}/openai-chat`,
            2: `${base}/openai-chat-phase-2`,
            3: `${base}/openai-chat-phase-3`,
        };


        return { url: urlByPhase[currentPhaseIndex] || 'http://localhost:8081/openai-chat' };
    }, [currentPhaseIndex]);


    // questa funzione intercetta le risposte in arrivo dal server, in questo modo puoi modificare il body della risposta prima che venga processata da DeepChat.
    // In particolare, qui stai facendo 2 cose importanti:
    // - stai salvando i dati importanti della risposta (brief, keywords, pillole, palette) nello state del progetto in modo da poterli mostrare nella UI e tenerli persistenti
    // - stai salvando l'id della risposta in previousResponseIdRef.current in modo da poterlo inviare come previous_response_id nella richiesta successiva (vedi requestInterceptor), in questo modo il server può lavorare in modo più efficiente senza dover processare tutta la cronologia della chat ad ogni messaggio
    const interceptorRef = useRef<(r: any) => any>(() => {
    })
    useEffect(() => {
        interceptorRef.current = (r: any) => {

            const phaseIndex = currentPhaseIndexRef.current;

            console.log("Response intercepted :", r);
            console.log("Current phase index (ref):", phaseIndex);

            if(phaseIndex === 1){ //sono nel brief creativo, prendi le keywords generate dal modello e usale come keywords sia per il brief creativo che per quello tecnico (in questo modo quando passo alla fase 2 ho già delle keywords da mostrare e modificare)
                if (Array.isArray(r?.keywords)) setKeywords(r.keywords)
                if (r?.brief) setBrief(r.brief)
                if (r?.brief_status) setBriefStatus(r.brief_status)
                if (Array.isArray(r?.pills)) setPillole(r.pills.slice(0, 2))
            }

            if(phaseIndex === 2){
                //se fase 2
                if (Array.isArray(r?.technicalKeywords)) setTechnicalKeywords(r.technicalKeywords)
                if (Array.isArray(r?.palette)) setPalette(r.palette)
            }




            // se la risposta presenda l'id allora salvalo come previousResponseId
            if(r?.id) {
                console.log("Setting previousResponseId to:", r.id);
                previousResponseIdRef.current = r.id;
            }


            applyAiResponseAndSave(phaseIndex, r)
                .then(() => console.log("Saved after AI response"))
                .catch(console.error);

            return r
        }
    }, [])

    //questa funzione intercetta le chiamate verso il server quando invii un messaggio, in questo modo puoi modificare il body della richiesta prima che venga inviata.
    // In particolare, qui stai facendo 2 cose importanti:
    // - stai salvando i messaggi in arrivo nello state del progetto (appendMessage) in modo da poterli mostrare nella UI e tenerli persistenti
    // - stai costruendo il body della richiesta in modo da inviare al server SOLO l'ultimo messaggio dell'utente e il previous_response_id, in questo modo il server può lavorare in modo più efficiente senza dover processare tutta la cronologia della chat ad ogni messaggio

    const requestInterceptor = useCallback((req: any) => {
            const nextReq = { ...req };

            console.log("Request before interceptor:", nextReq);
            console.log("Current phase index:", currentPhaseIndex);
            console.log("Current chat history before requestInterceptor:", chatHistoryRef.current);

            // body può essere string o object
            const bodyObj =
                typeof nextReq.body === "string"
                    ? JSON.parse(nextReq.body)
                    : { ...(nextReq.body || {}) };

            const incoming = Array.isArray(bodyObj.messages) ? bodyObj.messages : [];

            // ✅ salva nello state del progetto i messaggi user (text+html)
            appendIncomingMessages(currentPhaseIndex, incoming);

            // 1) prendi SOLO messaggi testuali (no html) per la logica del modello e per la tua chatHistoryRef
            const incomingText = toTextMessages(incoming);

            // 2) append evitando duplicati consecutivi
            for (const m of incomingText) {
                const last = chatHistoryRef.current[chatHistoryRef.current.length - 1];
                const isDup = last && last.role === m.role && last.text === m.text;

                if (!isDup) {
                    chatHistoryRef.current.push(m);
                }
            }

            console.log("Updated chat history after processing incoming messages:", chatHistoryRef.current);

            // se è definito previousResponseIdRef ed è diverso da null allora mando nel body solamente il previous_id e l'ultimo messaggio
            if (previousResponseIdRef.current) {
                console.log("Sending previous_response_id: ", previousResponseIdRef.current);
                bodyObj.previous_response_id = previousResponseIdRef.current;

                // invio nel bodyObj.messages solamente l'ultimo messaggio dell'utente
                const lastUserMessage = chatHistoryRef.current
                    .slice()
                    .reverse()
                    .find((m) => m.role === "user");

                bodyObj.messages = lastUserMessage ? [lastUserMessage] : [];
            } else {
                console.log("No previous_response_id defined, sending full chat history.");
                bodyObj.messages = chatHistoryRef.current;
            }

            // opzionale: manda la fase
            bodyObj.phase = currentPhaseIndex;

            nextReq.body = typeof nextReq.body === "string" ? JSON.stringify(bodyObj) : bodyObj;

            return nextReq;
        },
// ✅ MODIFICA 4: dipendenze corrette (stai usando appendMessage e toTextMessages)
        [currentPhaseIndex, toTextMessages]);


    const messageStyles = useMemo(() => ({
        default: {
            shared: {
                outerContainer: { margin: "12px 0" },
                bubble: {
                    borderRadius: "14px",
                    padding: "14px 16px",
                    lineHeight: "1.2",
                    fontSize: "14px",
                    /*border: "1px solid rgba(255,255,255,.06)",*/
                    boxShadow: "0 10px 30px rgba(0,0,0,.35)",
                },
            },
            ai: {
                bubble: {
                    maxWidth: '95%', width: '100%', marginTop: '10px',

                    color: "rgba(245,244,255,.95)",
                    paddingLeft: "52px",
                    borderRadius: "18px",

                    backgroundImage: `
      url("/assets/img/Stars.svg"),
      linear-gradient(90deg, rgba(35,14,70,.85), rgba(18,10,34,.85)),
      linear-gradient(rgba(12,6,22,1), rgba(12,6,22,1))
    `,
                    backgroundRepeat: "no-repeat, no-repeat, no-repeat",
                    backgroundPosition: "16px 16px, 0 0, 0 0",
                    backgroundSize: "18px 18px, cover, cover",
                },
            },
            user: {
                outerContainer: { justifyContent: "flex-end" },
                bubble: {
                    background: "rgba(255,220,210,.92)",
                    color: "rgba(30,10,25,.92)",
                    border: "1px solid rgba(255,77,26,.18)",
                    borderRadius: "16px",
                    padding: "14px 16px",
                    boxShadow: "0 10px 25px rgba(0,0,0,.20)",
                },
            },
        },
        html: {
            ai: {
                bubble: {
                    background: "linear-gradient(90deg, rgba(35,14,70,.75), rgba(18,10,34,.70))",
                    color: "rgba(245,244,255,.92)",
                    paddingLeft: "52px", // anche per la bubble che contiene i quick replies
                },
            },
        },


    }), []);


    const deepChatElRef = useRef<any>(null);

    const chatNode = useMemo(() => (
        <DeepChat
            ref={deepChatElRef}

            key={`phase-${currentPhaseIndex}`}
            history={initialHistory}                   // <-- history diversa per fase
            connect={connect}
            requestInterceptor={requestInterceptor}

            responseInterceptor={(r: any) => interceptorRef.current(r)}
            style={{ width: "580px", height: "100%" }}
            messageStyles={messageStyles}

        />
    ), [currentPhaseIndex, initialHistory, connect, messageStyles]);

    useEffect(() => {
        const el = deepChatElRef.current;
        if (!el) return;

        el.htmlClassUtilities = {
            feedback: {
                styles: {
                    default: {
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginTop: "10px",
                    },
                },
            },

            "feedback-text": {
                styles: {
                    default: {
                        width: "calc(100% - 92px)",
                        paddingTop: "2px",
                        color: "rgba(245,244,255,.85)",
                    },
                },
            },

            "feedback-icon": {
                styles: {
                    default: {
                        width: "42px",
                        height: "42px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "14px",

                        // ✅ contrasto su sfondo scuro
                        backgroundColor: "rgba(255,255,255,.8)",
                        border: "1px solid rgba(255,77,26,.45)",

                        // ✅ fondamentale: molte icone SVG seguono currentColor
                        color: "rgba(245,244,255,.95)",

                        boxShadow: "0 10px 24px rgba(0,0,0,.35), 0 0 0 1px rgba(0,0,0,.25) inset",
                        backdropFilter: "blur(8px)",
                        filter: "drop-shadow(0 0 10px rgba(255,77,26,.18))",
                    },
                    hover: {
                        backgroundColor: "rgba(255,77,26,.18)",
                        border: "1px solid rgba(255,77,26,.95)",
                        color: "rgba(255,255,255,1)",
                        transform: "translateY(-1px)",
                    },
                },
            },

            "feedback-icon-positive": {
                events: {

                    click: (e: any) => {
                        const el = e.target as HTMLElement;
                        const url = el.dataset?.url;
                        const phase = currentPhaseIndexRef.current;

                        if (url) {
                            console.log("Attachment positive:", url);
                            addAttachmentAndSave(phase, url,'image',"Image");
                        }
                    },
                },
                styles: {
                    default: {
                        // leggermente più “caldo”
                        color: "rgba(255,220,200,.98)",
                    },
                    hover: {
                        color: "rgba(255,255,255,1)",
                    },
                },
            },

            "feedback-icon-negative": {
                events: { click: () => console.log("negative response") },
                styles: {
                    default: {
                        transform: "rotate(180deg)",
                        marginLeft: "0px",
                        // un filo più “freddo” per differenziarlo
                        color: "rgba(210,210,255,.95)",
                    },
                    hover: {
                        color: "rgba(255,255,255,1)",
                    },
                },
            },
            "feedback-icon-generate-3d": {
                events: {
                    click: (e: any) => {
                        console.log("Generate 3D model clicked");
                        const el = e.target as HTMLElement;
                        const url = el.dataset?.url;
                        console.log("Data URL for 3D model generation:", url);

                        if (url) {
                            console.log("Generate 3D model:", url);
                            //vai alla fase 4 passando come parametro il link al modello 3D generato
                            setCurrentPhaseIndex(4);
                            setGeneratedImage(url)

                        }
                    },
                },
                styles: {
                    default: {
                        color: "rgba(255,200,220,.95)",
                    },
                    hover: {
                        color: "rgba(255,255,255,1)",
                    },
                },
            }

        };
    }, [currentPhaseIndex]);



    const history = [
        //{ role: 'user', text: 'Hey, how are you today?' },
        //{ role: 'ai', text: 'Come posso aiutarti?' },
    ];
    /* ====== UI ====== */


    const phase3Attachments =
        project?.phases?.find((p: any) => p.index === 3)?.attachments ?? [];


    const handleGenerate3D = useCallback(async (imgUrl: string) => {

        //load base url da config
        const cfg = (window as any).__APP_CONFIG__;
        const base = cfg.CHAT_BASE.replace(/\/$/, "");

            const res = await fetch(`${base}/generate3d`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ imageUrl: imgUrl, id: project?.project_id, phase: currentPhaseIndex }),
        });

        if (!res.ok) throw new Error(`generate3d failed (${res.status})`);

        const data = await res.json();
        const objUrl = data?.url || data?.objUrl;

        if (!objUrl) throw new Error("Risposta generate3d senza url .obj");

        // ✅ salva come attachment in fase 4
        await addAttachmentAndSave(4, objUrl,'3d-model',"3D Model");

        return objUrl as string;
    }, [addAttachmentAndSave]);


    return (
        <AppShell>
            <div className="sparcliRoot">
                <TopBar projectName={project?.title || "Project Name"} />

                <div className="layout3col">
                    <WipSidebar brief={brief} briefStatus={briefStatus} keywords={keywords} currentPhaseIndex={currentPhaseIndex} setCurrentPhaseIndex={setCurrentPhaseIndex}/>
                    {/*<main className="chatArea glassCard">
                        <div className="chatHeader">
                            <div className="chatHeaderLeft">
                                <span className="chatPhase">{`Fase ${currentPhaseIndex} di 5`}</span>
                                <span className="chatTitle">{currentPhase}</span>
                            </div>

                            <div className="chatHeaderRight">
                                <button className="chatIconBtn"  aria-label="Menu">⋯</button>
                                <button className="chatIconBtn" aria-label="Chiudi">×</button>
                            </div>
                        </div>

                        <div className="chatBody">

                            <DeepChat
                                ref={deepChatRef} // Attach the ref to DeepChat

                                requestBodyLimits={{
                                    totalMessagesMaxCharLength: 1000,
                                    maxMessages: 20
                                }}

                                //key={`phase-${currentPhaseIndex}`}
                                //history={initialHistory}                   // <-- history diversa per fase
                                connect={connect}
                                //responseInterceptor={(r: any) => interceptorRef.current(r)}
                                style={{ width: "580px", height: "100%" }}
                                //messageStyles={messageStyles}

                            />

                        </div>
                    </main>
*/}
                    {/*<ChatPanel only for phase 1,2,3 */}
                    {(currentPhaseIndex === 1 || currentPhaseIndex === 2 || currentPhaseIndex === 3) && (
                    <ChatPanel
                        chatNode={chatNode}
                        phaseLabel={`Fase ${currentPhaseIndex} di 4`}
                        phaseTitle={currentPhase}
                        backDisabled={currentPhaseIndex === 1}
                        onBack={() =>
                            setCurrentPhaseIndex(Math.max(1, currentPhaseIndex - 1))
                        }
                        onMenu={() => console.log("menu")}
                        onClose={() => console.log("close")}
                    />
                    )}


                    {/*GeneratorPanel for phase 4*/}
                    {currentPhaseIndex === 4 && (
                        <GeneratorPanel
                            phaseLabel={`Fase ${currentPhaseIndex} di 4`}
                            phaseTitle={currentPhase}
                            imgUrl={generatedImage || "/assets/img/placeholder-3d.png"}
                            backDisabled={false}
                            onBack={() => setCurrentPhaseIndex(Math.max(1, currentPhaseIndex - 1))}
                            onGenerate3D={handleGenerate3D}
                        />
                    )}

                    <PillsSidebar
                        pillole={pillole}
                        palette={palette}
                        technicalKeywords={technicalKeywords}
                        currentPhaseIndex={currentPhaseIndex}
                        setCurrentPhaseIndex={setCurrentPhaseIndex}
                        attachments={phase3Attachments}/>

                </div>
                <Footer />

            </div>
        </AppShell>
    );
}
